import { useQuery } from '@apollo/client';
import memoize from 'lodash/memoize';
import React, { useState, useCallback } from 'react';
import {
  InvoicePaymentMethod,
  SortDirection,
  FetchPricingHistoryForEntitlementQuery,
  FetchPricingHistoryForEntitlementQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  InvoiceWithLineItems,
  PaymentMethods,
  InvoiceLineItem,
  InvoiceLineItemDiscount,
} from '@atlassian/cssxp-types-billing';
import { FormattedDate, FormattedCurrencyV2 } from '../../../formattedV2';
import { ColGroup, TableBody, TableHead } from '../../../search-results-table';
import { NoDataContentInTable } from '../../../no-data-content-table';
import { useFeatureToggle } from '../../../hooks';

import { fetchPricingHistoryForEntitlement } from './queries/fetch-pricing-history-for-entitlement';
import { CustomDynamicResultTable } from '../../../custom-dynamic-result-table';
import { SourceSystem, NEW_DESIGN_DATE_FORMAT, INVOICE_PATH_2 } from '@atlassian/cssxp-typescommon';
import { withApollo } from '../../../graphql-provider';
import { isProductSkipTrialEligible } from '@atlassian/cssxp-utils-product';
import sumBy from 'lodash/sumBy';
import toNumber from 'lodash/toNumber';
import { capitalize } from 'lodash';
import { Link } from 'react-router-dom';

export const DEFAULT_ROWS_PER_PAGE = 8;
export const DEFAULT_PAGE = 1;

const getColumns = () => {
  const columns = [
    {
      name: 'Plan | Type | Status',
      customClassNames: 'col-40',
    },
    {
      name: 'Billing period',
      customClassNames: 'col-30',
    },
    {
      name: 'Allocated to',
      customClassNames: 'col-20',
    },
    {
      name: 'Price',
      customClassNames: 'col-20',
    },
    {
      name: 'Adjustment',
      customClassNames: 'col-20',
    },
    {
      name: 'Net paid',
      customClassNames: 'col-20',
    },
    {
      name: 'Paid',
      customClassNames: 'col-20',
    },
    {
      name: '',
      customClassNames: 'col-2',
    },
  ];

  return columns;
};

const sortOptions = {
  sortFields: ['createdDate', 'id'],
  sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC],
};

interface Props {
  label: string;
  transactionId?: string;
  entitlementId: string;
  invoiceRedirectPath?: string;
}

export const PricingHistoryDetailsTab = ({ entitlementId, transactionId, invoiceRedirectPath }: Props) => {
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const { refetch, loading, error, data } = useQuery<
    FetchPricingHistoryForEntitlementQuery,
    FetchPricingHistoryForEntitlementQueryVariables
  >(fetchPricingHistoryForEntitlement, {
    variables: {
      id: entitlementId,
      searchOptions: {
        first: rowsPerPage,
        offset: 0,
        sort: sortOptions,
      },
      //@ts-ignore
      filters: transactionId
        ? {
            transactionAccountId: transactionId,
          }
        : undefined,
    },
    displayName: 'fetchPricingHistoryForEntitlement',
    notifyOnNetworkStatusChange: true,
  });
  const [isEnabled] = useFeatureToggle();
  // variables to use for paging and populating our template
  const totalCount = data?.entitlementDetails.pricingPlanHistory.totalCount ?? 0;
  const hasNoResults = totalCount === 0;
  const nodes = data?.entitlementDetails?.pricingPlanHistory?.nodes;

  const adjustedAmount = (subtotal: string | null | undefined, discount: InvoiceLineItemDiscount[] | null): number => {
    const actualSubtotal = toNumber(subtotal);
    const actualDiscount = sumBy(discount, 'amount');
    if (!isNaN(actualSubtotal) && actualSubtotal !== 0 && !isNaN(actualDiscount)) {
      return actualSubtotal - actualDiscount;
    }

    return 0;
  };

  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
          sort: sortOptions,
        },
      });
    },
    [rowsPerPage, refetch]
  );

  const handleRowChange = useCallback((rowsCount: number) => {
    setRowsPerPage(rowsCount);
    setCurrentPage(1);
  }, []);

  const props = {
    children: (
      <>
        <ColGroup columns={getColumns()} />
        <TableHead columns={getColumns()} />
        <TableBody>
          {/*@ts-ignore*/}
          {(nodes ?? []).map((invoice: InvoiceWithLineItems, index: number) => {
            const { invoiceNumber, paidDate } = invoice;

            const checkMultiProduct = invoice.lineItems.filter((invoiceItemObj) =>
              isProductSkipTrialEligible(invoiceItemObj.productKey)
            );
            const unitLabel = checkMultiProduct.length === invoice.lineItems.length ? 'Users' : '';

            const getLineItemKey = (item: InvoiceLineItem) => {
              // All these fields are potentially null so use the
              // combination of them to create a unique key for React
              const identifier =
                invoice.sourceSystem === SourceSystem.CCP ? item.entitlementNumber || item.id : item.sen;
              const description = item.description ?? '';
              const amount = item.amount ?? '';

              return `${identifier}-${description}-${amount}`;
            };

            const getFormattedPlanDetails = (pricingPlan: any) => {
              return `${pricingPlan.description} |
                ${capitalize(pricingPlan.pricingPlanType).replace(/_/g, ' ')} | ${capitalize(pricingPlan.status)}`;
            };

            return (
              <React.Fragment key={index}>
                {invoice?.lineItems.map((item: InvoiceLineItem) => (
                  <React.Fragment key={getLineItemKey(item)}>
                    <tr className="line-item">
                      <td>{item.pricingPlan ? getFormattedPlanDetails(item.pricingPlan) : <NoDataContentInTable />}</td>
                      <td>
                        <FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={item.startDate} /> -{' '}
                        <FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={item.endDate} />
                      </td>
                      <td data-testid="quantity-cell">
                        <span>{item.quantity ? `${item.quantity} ${unitLabel}` : <NoDataContentInTable />}</span>
                      </td>
                      <td data-testid="item-amount-cell">
                        <FormattedCurrencyV2
                          value={item.subtotal ? item.subtotal : 0}
                          currency={invoice.paymentCurrency}
                          showCurrencyCode
                        />
                      </td>
                      <td>
                        {item.discounts?.map((discount: InvoiceLineItemDiscount, index: number) => (
                          <div key={index}>
                            <FormattedCurrencyV2
                              key={`${discount.amount}-${index}`}
                              value={discount.amount ? discount.amount : 0}
                              currency={invoice.paymentCurrency}
                              showCurrencyCode
                            />
                          </div>
                        ))}
                      </td>
                      <td>
                        <FormattedCurrencyV2
                          value={adjustedAmount(item.subtotal, item.discounts)}
                          currency={invoice.paymentCurrency}
                          showCurrencyCode
                        />
                      </td>
                      <td>
                        <FormattedDate dateFormat={NEW_DESIGN_DATE_FORMAT} date={paidDate} />
                        {invoiceNumber && (
                          <div>
                            <Link
                              to={
                                invoiceRedirectPath
                                  ? `${invoiceRedirectPath}${invoiceNumber}`
                                  : `${INVOICE_PATH_2}/${invoiceNumber}`
                              }
                              data-testid="invoice-link"
                            >
                              {invoiceNumber}
                            </Link>
                          </div>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
        </TableBody>
      </>
    ),
    noResults: hasNoResults,
    isLoading: loading,
    error: error?.toString(),
    selectableResults: true,
    isPaginationEnabled: totalCount > DEFAULT_ROWS_PER_PAGE,
    totalCount: totalCount,
    rowsPerPage: rowsPerPage,
    currentPage: currentPage,
    onPageChange: onPageChange,
    onRowChange: handleRowChange,
    testId: 'pricing-history-table',
    emptyStateProps: {
      header: '',
      description: 'No pricing history found',
    },
    className: 'pricing-history-details',
  };

  return (
    <div className="pricing-history-table">
      <CustomDynamicResultTable {...props}></CustomDynamicResultTable>
    </div>
  );
};

export default withApollo(PricingHistoryDetailsTab);
